import React from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import EntryButtons from '@/components/UI/ButtonGroup/ButtonGroup';
import SimpleButton from '@/components/UI/SimpleButton/SimpleButton';
import { CENTER_VIEWER_CR, statusCampaign } from '@/helpers/constants';
import useCheckDisabled from '@/hooks/useCheckDisabled';
import { setVolumesAndOriginChanged } from '@/redux/Campaign/campaign.action';
import {
  getCampaignVehicles, getOriginalValues, getCampaignInfo, getCampaignVehiclesVolumes, getVolumeHeaderPlants,
  getSelectedPlants,
} from '@/redux/Campaign/campaign.reducer';
import { AffectedVehicles, AffectedVehiclesOriginalValues, CampaignInfo, Plant, Volume } from '@/types';
import { VOLUME_FIELDS } from '../VolumeDetails/VehicleCleanDirt/VehicleCleanDirt-utils';
import { getOriginValuesNewState } from '../VolumeDetails/volumeDetails-utils';
import { isViaPlants } from '../../SeriesPanel/SeriesRow/series-row-utils';

interface VolumeActionsProps {
  affectedVehicles: AffectedVehicles;
  originalValues: AffectedVehiclesOriginalValues;
  headerPlants: Plant[];
  volumes: Volume[];
  plants: Plant[];
  plantSelected: Plant;
  setShowCopyData: (active: boolean) => void;
  manualEntry: boolean;
  setManualEntry: (active: boolean) => void;
  setVolumesOriginhadChanged: (changed: any) => void;
  campaignInfo: CampaignInfo;
  defaultLanguage: string;
}

const VolumeActions: React.FC<VolumeActionsProps> = (props) => {
  const { affectedVehicles, originalValues, headerPlants, volumes, plants, plantSelected, setShowCopyData, manualEntry,
    setManualEntry, setVolumesOriginhadChanged, campaignInfo, defaultLanguage } = props;
  const { t } = useTranslation();
  const isCenterViewer = campaignInfo.authorization.kindOfPermission === CENTER_VIEWER_CR;
  const checkDisabled = useCheckDisabled(true);

  const getVolume = (plant = isViaPlants(affectedVehicles) ? plants[0] : headerPlants[0]) => volumes
    .find((currentVolume) => currentVolume.plant === plant?.wkzNumber);
  const { selected, comments, nOKOK } = VOLUME_FIELDS;

  const getSelectedVolume = () => {
    const selectedVolume = volumes?.find((vol: Volume) => vol.plant === plantSelected?.wkzNumber);

    return selectedVolume === undefined
      ? originalValues?.values?.volumes?.find((vol: Volume) => vol.plant === plantSelected?.wkzNumber)
      : selectedVolume;
  };

  const checkPlantListNotEmpty = () => {
    const selectedVolume = getSelectedVolume();

    if (selectedVolume !== undefined) {
      const myEntry = selectedVolume.limitationVolumeManualEntry;

      return selected.some((selected) => myEntry?.[selected]?.length > 0)
        || comments.some((comment) => myEntry?.[comment]?.[defaultLanguage]);
    }
  };

  const checkNokOkNotEmpty = () => {
    const selectedVolume = getSelectedVolume();

    if (!selectedVolume) return false;

    return selectedVolume.limitationVolumeManualEntry?.vehicleGroups.some((group) => (
      nOKOK.some((option) => group.dirtPoint?.[option]?.length > 0 || group.cleanPoint?.[option]?.length > 0)
    ));
  };

  const saveAffectedVehicles = (fileOptionSelected: boolean) => {
    if (plantSelected) {
      const selectedVolume = {
        ...getVolume(plantSelected),
        fileOptionSelected,
      };

      const updatedVolumes = volumes?.map((volume) => ((volume.plant === plantSelected?.wkzNumber) ? selectedVolume : volume));
      setVolumesOriginhadChanged({
        originalValues: getOriginValuesNewState(originalValues, plantSelected),
        volumes: updatedVolumes,
      });
    }
  };

  const checkAllEmpty = () => manualEntry && (checkNokOkNotEmpty() || checkPlantListNotEmpty());

  const changeManualEntryUploadFile = (value: boolean, type: string) => {
    const volumeFound = getVolume(plantSelected);

    if (type === 'manual-entry' && volumeFound?.vehicleRestrictionFiles?.length === 0) {
      volumeFound.fileOptionSelected = false;
      setManualEntry(value);
      saveAffectedVehicles(false);
    } else if (type === 'upload-file' && volumeFound) {
      volumeFound.fileOptionSelected = true;
      setManualEntry(value);
      saveAffectedVehicles(true);
    }
  };

  const handleClickCopyPlant = () => {
    setShowCopyData(true);
  };

  const displayCopyPlantData = () => getSelectedVolume() && getSelectedVolume()?.vehicleRestrictionFiles?.length > 0
    && campaignInfo.status === statusCampaign.IN_CREATION && !isCenterViewer;

  const checkManualEntryDisabled = isViaPlants(affectedVehicles)
    || getVolume(plantSelected)?.vehicleRestrictionFiles?.length !== 0;

  return (
    <>
      <EntryButtons
        id="button-group-manual-entry"
        leftButton={{
          label: t('affectedVehicles.manualEntry'),
          id: 'manual-entry',
          checked: manualEntry,
          disabled: checkDisabled || checkManualEntryDisabled,
          disabledMsg: t('affectedVehicles.onlyPlantsErrorMessage'),
        }}
        rightButton={{
          label: t('affectedVehicles.uploadVehicleViaFile'),
          id: 'upload-file',
          checked: !manualEntry,
          disabled: checkDisabled,
          disabledMsg: t('affectedVehicles.onlyPlantsErrorMessage'),
        }}
        onClickAction={changeManualEntryUploadFile}
      />
      {checkAllEmpty() && !checkDisabled && (
        <SimpleButton
          id="copy-plant-data"
          color="primary"
          buttonText={t('affectedVehicles.buttonCopyPlantData')}
          handleClick={handleClickCopyPlant}
        />
      )}
      {displayCopyPlantData() && (
        <SimpleButton
          id="copy-plant-file"
          color="primary"
          buttonText={t('affectedVehicles.copyPlantFile')}
          handleClick={handleClickCopyPlant}
        />
      )}
    </>
  );
};

const mapStateToProps: any = (state) => ({
  headerPlants: getVolumeHeaderPlants(state),
  volumes: getCampaignVehiclesVolumes(state),
  affectedVehicles: getCampaignVehicles(state),
  originalValues: getOriginalValues(state),
  defaultLanguage: getCampaignInfo(state)?.defaultLanguage,
  plants: getSelectedPlants(state) || [],
  campaignInfo: getCampaignInfo(state),
});

const actionCreators = {
  setVolumesOriginhadChanged: setVolumesAndOriginChanged,
};

export default connect(mapStateToProps, actionCreators)(VolumeActions);
